import {
  BEXIO_CONTACT_TYPE,
  type BexioContactMigrationEntry,
  type BexioContactMigrationParams,
  type BexioContactRef,
  type BexioDuplicateContact,
} from '../models/bexioContactMigration.model';

/** `tenantId:bexioId`, the format the backend expects in `exclude`. */
export function bexioContactRefKey({ tenantId, bexioId }: BexioContactRef): string {
  return `${tenantId}:${bexioId}`;
}

/**
 * Query params for the convert endpoint. The backend treats a missing dryRun as a dry run.
 * Throws instead of dropping an exclusion it cannot express, so a contact is never converted by accident.
 */
export function buildBexioContactMigrationQuery({
  dryRun,
  excludedContacts = [],
}: BexioContactMigrationParams): Record<string, string> {
  const query: Record<string, string> = { dryRun: String(dryRun) };
  const exclude = excludedContacts.map(contact => {
    const tenantId = contact.tenantId?.trim();
    if (!tenantId || tenantId.includes(',') || !Number.isInteger(contact.bexioId) || contact.bexioId <= 0) {
      throw new Error(`Cannot exclude Bexio contact ${contact.bexioId} without a valid tenant.`);
    }
    return bexioContactRefKey({ tenantId, bexioId: contact.bexioId });
  });
  const unique = [...new Set(exclude)].sort();
  if (unique.length > 0) {
    query.exclude = unique.join(',');
  }
  return query;
}

/** Same contact id can exist in different tenants' Bexio accounts. */
export function bexioEntryKey(entry: BexioContactMigrationEntry): string {
  return bexioContactRefKey(entry);
}

export function formatPersonName(entry: BexioContactMigrationEntry): string {
  return [entry.newName2, entry.newName1]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');
}

export function formatCompanyName(entry: BexioContactMigrationEntry): string {
  return [entry.currentName1, entry.currentName2]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');
}

/** Contacts the dry run would convert, sorted by the name they will get. */
export function getConvertibleEntries(
  entries: BexioContactMigrationEntry[]
): BexioContactMigrationEntry[] {
  return entries
    .filter(entry => entry.status === 'wouldConvert')
    .sort((a, b) =>
      formatPersonName(a).localeCompare(formatPersonName(b), 'de-CH')
    );
}

export function getEntriesWithStatus(
  entries: BexioContactMigrationEntry[],
  ...statuses: BexioContactMigrationEntry['status'][]
): BexioContactMigrationEntry[] {
  return entries.filter(entry => statuses.includes(entry.status));
}

/** Person as "first last" (name_2 name_1), company as "name_1 name_2". */
export function formatDuplicateName(contact: BexioDuplicateContact): string {
  const parts =
    contact.contactTypeId === BEXIO_CONTACT_TYPE.PERSON
      ? [contact.name2, contact.name1]
      : [contact.name1, contact.name2];
  return parts
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');
}

/** Company contacts with a second account on the same email, sorted by the name they would get. */
export function getDuplicateEntries(
  entries: BexioContactMigrationEntry[]
): BexioContactMigrationEntry[] {
  return entries
    .filter(entry => entry.status === 'duplicate')
    .sort((a, b) =>
      formatPersonName(a).localeCompare(formatPersonName(b), 'de-CH')
    );
}
